import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Lock, Mail, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { useAuthStore } from '../../store/authStore';
import { MFASetupForm } from './MFASetupForm';
import api from '../../lib/api';

interface LoginFormData {
  email: string;
  password: string;
  mfaToken?: string;
}

interface LoginFormProps {
  onSuccess?: () => void;
  onForgotPassword?: () => void;
  onRegister?: () => void;
}

export function LoginForm({ onSuccess, onForgotPassword, onRegister }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [duoAvailable, setDuoAvailable] = useState(false);
  const [duoTxid, setDuoTxid] = useState<string | null>(null);
  const [requiresMFASetup, setRequiresMFASetup] = useState(false);
  const [totpAvailable, setTotpAvailable] = useState<boolean>(true);
  const [loginData, setLoginData] = useState<{ email: string; password: string } | null>(null);
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<LoginFormData>();
  const { login, isLoading } = useAuthStore();
  const { toast } = useToast();

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result = await login(data.email, data.password, data.mfaToken);
      
      if (result.requiresMFA) {
        setRequiresMFA(true);
        setLoginData({ email: data.email, password: data.password });
        setDuoAvailable(Boolean((result as any).duoAvailable));
        setTotpAvailable((result as any).totpAvailable !== false);
        toast({
          title: "MFA Required",
          description: duoAvailable && !totpAvailable ? 'Approve the Duo push notification on your mobile.' : 'Please enter your 6-digit authentication code or use Duo Push.',
        });
        return;
      }

      if (result.requiresMFASetup) {
        setLoginData({ email: data.email, password: data.password });
        setRequiresMFASetup(true);
        toast({
          title: "MFA Setup Required",
          description: "Administrator has enabled MFA for your account. Please set up your authenticator.",
        });
        return;
      }

      toast({
        title: "Login Successful",
        description: "Welcome back to ISMS Manager!",
      });
      
      onSuccess?.();
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error instanceof Error ? error.message : "An error occurred during login",
        variant: "destructive",
      });
    }
  };

  const handleBackToLogin = () => {
    setRequiresMFA(false);
    setValue('mfaToken', '');
    setDuoAvailable(false);
    setDuoTxid(null);
  };

  const handleUseDuo = async () => {
    if (!loginData) {
      toast({ title: 'Missing credentials', description: 'Please go back and enter email and password again.', variant: 'destructive' });
      return;
    }
    const { email, password } = loginData;
    try {
      toast({ title: 'Duo Push Sent', description: 'Approve login in Duo Mobile.' });
      const resp = await api.startDuo(email, password);
      if ((resp as any).token) {
        await useAuthStore.getState().checkAuth();
        toast({ title: 'Login Successful', description: 'Welcome back to ISMS Manager!' });
        onSuccess?.();
        return;
      }
      const txid = (resp as any).txid as string;
      setDuoTxid(txid);
      const interval = setInterval(async () => {
        try {
          const res = await api.checkDuo(email, password, txid);
          if ((res as any).token) {
            clearInterval(interval);
            await useAuthStore.getState().checkAuth();
            toast({ title: 'Login Successful', description: 'Welcome back to ISMS Manager!' });
            onSuccess?.();
            return;
          }
          // if still pending, keep waiting
        } catch (e) {
          clearInterval(interval);
        }
      }, 2000);
    } catch (error) {
      toast({ title: 'Duo Error', description: error instanceof Error ? error.message : 'Failed to start Duo', variant: 'destructive' });
    }
  };

  const handleMFASetupSuccess = async (mfaSecret: string, authToken?: string) => {
    if (!loginData) return;

    // MFA setup completed successfully, user should be logged in
    toast({
      title: "MFA Setup Complete",
      description: "Your account is now secured with MFA. Login successful!",
    });
    
    // Reset states and call success callback
    setRequiresMFASetup(false);
    setLoginData(null);
    onSuccess?.();
  };

  const handleBackFromMFASetup = () => {
    setRequiresMFASetup(false);
    setLoginData(null);
  };

  // Show MFA setup if required
  if (requiresMFASetup && loginData) {
    return (
      <MFASetupForm
        onSuccess={handleMFASetupSuccess}
        onBack={handleBackFromMFASetup}
        isRegistration={false}
        isPostLogin={true}
        email={loginData.email}
        password={loginData.password}
      />
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">
          {requiresMFA ? "Two-Factor Authentication" : "Welcome Back"}
        </CardTitle>
        <CardDescription className="text-center">
          {requiresMFA
            ? (duoAvailable && !totpAvailable
                ? "Approve the Duo push notification in Duo Mobile"
                : "Enter the 6-digit code from your authenticator app")
            : "Sign in to your ISMS Manager account"}
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {!requiresMFA && (
            <>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    className="pl-10"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                    {...register('password', {
                      required: 'Password is required'
                    })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>
            </>
          )}

          {requiresMFA && totpAvailable && (
            <div className="space-y-2">
              <Label htmlFor="mfaToken">Authentication Code</Label>
              <div className="relative">
                <Shield className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="mfaToken"
                  type="text"
                  placeholder="000000"
                  maxLength={6}
                  className="pl-10 text-center tracking-widest"
                  {...register('mfaToken', {
                    required: requiresMFA ? 'MFA token is required' : false,
                    pattern: {
                      value: /^\d{6}$/,
                      message: 'Token must be 6 digits'
                    }
                  })}
                  autoComplete="one-time-code"
                />
              </div>
              {errors.mfaToken && (
                <p className="text-sm text-destructive">{errors.mfaToken.message}</p>
              )}
              {duoAvailable && (
                <Button type="button" variant="outline" className="w-full" onClick={handleUseDuo}>
                  Use DUO Push
                </Button>
              )}
            </div>
          )}
          {requiresMFA && !totpAvailable && duoAvailable && (
            <div className="space-y-2">
              <Button type="button" className="w-full" onClick={handleUseDuo}>
                Use DUO Push
              </Button>
            </div>
          )}

          <div className="space-y-2">
            {(!requiresMFA || totpAvailable) && (
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : requiresMFA ? "Verify" : "Sign In"}
              </Button>
            )}

            {requiresMFA && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleBackToLogin}
              >
                Back to Login
              </Button>
            )}
          </div>

          {!requiresMFA && (
            <div className="space-y-2">
              <Button
                type="button"
                variant="ghost"
                className="w-full text-sm"
                onClick={onForgotPassword}
              >
                Forgot your password?
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto font-normal"
                  onClick={onRegister}
                >
                  Sign up
                </Button>
              </div>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
